import { Box } from "@mui/material";
import * as React from "react";
import {
    IdTitleNode,
    IIdTitleNode,
} from "ui/Scripts/source/models/common/idTitleNode";
import { EventTypeCategory } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../../framework/appUtils";
import * as eventService from "../../../../models/services/eventService";
import { DialogSectionCard } from "../../../framework/Dialog/DialogSectionCard";
import { List } from "../../../framework/List/List";
import { ListIconButtonsItem } from "../../../framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { SelectIdTitleNodesDialog } from "../../../framework/Select/SelectIdTitleNodeDialog";
import { useEffect, useState } from "react";

export interface EventEditDialogIdentificationEventTypeListProp {
  isReadOnly: boolean;
  screenHeight: number;
  companyId: string;
  eventId: string;
  eventTypeCategory: EventTypeCategory;
  eventTypes: IIdTitleNode[];
  eventTypeIds: string[];
  onChange: (eventTypeIds: string[]) => void;
}

interface IEventEditDialogIdentificationEventTypeListState {
  // List
  items: IIdTitleNode[];
  selectedId: string;
  checkedIds: string[];

  // Dialog
  showSelectEventTypesDialog: boolean;
}

export const EventEditDialogIdentificationEventTypeList = (props: EventEditDialogIdentificationEventTypeListProp) => {
    const getItems = (): IIdTitleNode[] => {
        if (!props.eventTypes || props.eventTypes.length < 0 || !props.eventTypeIds || props.eventTypeIds.length < 0) {
            return [];
        }
        const result: IIdTitleNode[] = props.eventTypes.map((i) => new IdTitleNode(i));
        IdTitleNode.setHierarchialTitles(result);
        IdTitleNode.sortUIdTitleNodes(result);
        return IdTitleNode.getByIds(result, props.eventTypeIds);
    };

    const [state, setState] = useState<IEventEditDialogIdentificationEventTypeListState>(
        {
            items: getItems(),
            selectedId: null,
            checkedIds: [],
            showSelectEventTypesDialog: false,
        }
    );

    useEffect(() => {
        setState(old => ({
            ...old,
            selectedId: null,
            checkedIds: []
        }));
    }, [props.companyId ?? ""]);

    useEffect(() => {
        setState(old => ({
            ...old,
            items: getItems()
        }));
    }, [(props.eventTypeIds ?? []).join(",")]);

    const handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        setState(old => ({
            ...old,
            selectedId,
            checkedIds,
        }));
    };

    // #region Add
    const handleSelectIdTitleNodesDialogOk = async (selectedIds: string[]) => {
        if (!selectedIds) return;
        if (props.eventId) {
            //Save to Db
            const result = await AppUtils.callService(() =>
                eventService.eventEditSaveEventTypes(
                    props.companyId,
                    props.eventId,
                    props.eventTypeCategory,
                    selectedIds
                )
            );
            if (!result) return;
        }
        setState(old => ({
            ...old,
            showSelectEventTypesDialog: false,
        }));
        props.onChange(selectedIds);
    };

    const onSelectIdTitleNodesDialogCancel = () => {
        setState(old => ({
            ...old,
            showSelectEventTypesDialog: false,
        }));
    };

    const handleAdd = () => {
        setState(old => ({
            ...old,
            showSelectEventTypesDialog: true,
        }));
    };
    // #endregion Add

    // #region Remove
    const handleRemove = async () => {
        const selectedIds = List.getSelectedIds(state.selectedId, state.checkedIds);
        if (selectedIds.length < 1) return;
        const newEventTypeIds = props.eventTypeIds.filter(
            (i) => selectedIds.indexOf(i) < 0
        );
        if (props.eventId) {
            //Save to Db
            const result = await AppUtils.callService(() =>
                eventService.eventEditSaveEventTypes(
                    props.companyId,
                    props.eventId,
                    props.eventTypeCategory,
                    newEventTypeIds
                )
            );
            if (!result) return null;
        }
        props.onChange(newEventTypeIds);
    };
    // #endregion Remove

    return (
        <DialogSectionCard
            headerTitle={props.eventTypeCategory === EventTypeCategory.A
                ? Translations.EventTypes
                : props.eventTypeCategory === EventTypeCategory.B
                    ? Translations.EventTypeBs
                    : Translations.EventTypeCs}
            buttons={
                <ListIconButtonsItem
                    addTooltip={
                        props.eventTypeCategory === EventTypeCategory.A
                            ? Translations.AddEventType
                            : props.eventTypeCategory === EventTypeCategory.B
                                ? Translations.AddEventTypeB
                                : Translations.AddEventTypeC
                    }
                    removeTooltip={
                        props.eventTypeCategory === EventTypeCategory.A
                            ? Translations.RemoveEventType
                            : props.eventTypeCategory === EventTypeCategory.B
                                ? Translations.RemoveEventTypeB
                                : Translations.RemoveEventTypeC
                    }
                    removeDisabled={!state.selectedId && state.checkedIds.length < 1}
                    onAdd={!props.isReadOnly ? handleAdd : null}
                    onRemove={!props.isReadOnly ? handleRemove : null}
                />
            }
        >
            <Box sx={{ margin: "0 -1rem" }}>
                <List
                    intialized={true}
                    isLoading={false}
                    hasMore={false}
                    height={props.screenHeight === 0
                        ? "calc(100vh - 680px)"
                        : (props.screenHeight === 1
                            ? "calc(100vh - 628px)"
                            : "calc(100vh - 605x)"
                        )
                    }
                    lines={state.items.map((item) => {
                        return {
                            columns: [{ classes: "col-12", content: item.title }],
                            id: item.id,
                        };
                    })}
                    noDataMessage={
                        props.eventTypeCategory === EventTypeCategory.A
                            ? Translations.AddEventType
                            : props.eventTypeCategory === EventTypeCategory.B
                                ? Translations.AddEventTypeB
                                : Translations.AddEventTypeC
                    }
                    sortColumn={""}
                    sortOrderIsAsc={true}
                    selectedId={state.selectedId}
                    checkedIds={state.checkedIds}
                    onSetSelectedIds={handleSetSelectedIds}
                    onSearchLines={null}
                />
            </Box>
            {state.showSelectEventTypesDialog && (
                <SelectIdTitleNodesDialog
                    toggleAllId={""}
                    nodes={props.eventTypes}
                    title={
                        props.eventTypeCategory === EventTypeCategory.A
                            ? Translations.SelectEventType
                            : props.eventTypeCategory === EventTypeCategory.B
                                ? Translations.SelectEventTypeB
                                : Translations.SelectEventTypeC
                    }
                    selectedIds={props.eventTypeIds}
                    onOk={handleSelectIdTitleNodesDialogOk}
                    onCancel={onSelectIdTitleNodesDialogCancel}
                />
            )}
        </DialogSectionCard>
    );
};
